import {TypedNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/_Base';
import {ArrayUtils} from '../../../../../@polygonjs/polygonjs/src/core/ArrayUtils';
import {SceneCodeExporter} from './Scene';
import {NodesCodeExporterOption, NodesCodeExporter} from './Nodes';
import {CodeExporterDispatcher} from './Dispatcher';
import {BaseNodeByContextMap, NodeContext} from '../../../../../@polygonjs/polygonjs/src/engine/poly/NodeContext';
// import {NodeTypeMap} from '../../../../../@polygonjs/polygonjs/src/engine/containers/utils/ContainerMap';
import {ensureNotAReservedWord} from './ReservedWords';
import {PolyNodeController} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/utils/poly/PolyNodeController';
import {SetInputsOptions} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/utils/io/InputsController';

interface PositionData {
	position_x_offset?: number;
}
const setInputsOptions: SetInputsOptions = {
	noExceptionOnInvalidInput: true,
};
const setInputsOptionsJSON = JSON.stringify(setInputsOptions);

export class NodeCodeExporter<NC extends NodeContext> {
	protected _lines: string[] = [];
	constructor(protected _node: TypedNode<NC, any>, protected options: NodesCodeExporterOption) {}

	create(parent_var_name?: string): string[] {
		this.reset();

		if (!this.is_root()) {
			this.add_create(parent_var_name);
			this.add_name();
		}
		if (!this.options.ignoreGrandChildren) {
			this._preAddChildren();
			this.add_children();
			this.add_selection();
		}

		this.add_comment();
		this.add_position({});
		this.add_input_clonable_state();
		this.add_flags();
		this.add_params();
		this.add_custom();

		// already called in create_node()
		// this._lines.push(`${this.var_name()}.lifecycle.set_creation_completed();`);

		return this._lines;
	}
	protected _preAddChildren() {}

	set_up(options: object = {}): string[] {
		this.reset();
		this.add_input();
		// this.add_position(options)
		// this.add_bypass_flag()
		// this.add_params()
		// this.add_children()
		// this.add_custom()
		// this.add_selection()

		return this._lines;
	}
	// children_and_selection(): string[]{
	// 	this.reset()
	// 	this.add_children()
	// 	this.add_selection()
	// }
	create_function_declare(parent_var_name: string): string[] {
		this._node.scene().nodesController.registerNodeContextSignature(this._node);

		const ls: string[] = [];
		ls.push(`function ${this.function_name()}(${parent_var_name}){`);
		this.create(parent_var_name).forEach((l) => {
			ls.push(`	${l}`);
		});
		ls.push(`	return ${this.var_name()}`);
		ls.push(`}`);
		return ls;
	}
	create_function_call(parent_var_name?: string): string {
		if (parent_var_name == null) {
			const parent = this._node.parent();
			if (parent) {
				parent_var_name = CodeExporterDispatcher.dispatch_node(parent, this.options).var_name(); //.visit(CodeExporterVisitor).var_name()
			}
		}
		return `var ${this.var_name()} = ${this.function_name()}(${parent_var_name})`;
	}

	var_name(): string {
		if (this.is_root()) {
			const scene_exporter = new SceneCodeExporter(this._node.scene());
			return `${scene_exporter.var_name()}.root`;
		} else {
			// add a _ in front in case the name starts by a number
			// return `context._${this._node.path().replace(/\//g, '_').substr(1)}`;
			let varName = this._node.name().replace(/\//g, '_');
			// ensure it is not a reserved javascript word
			varName = ensureNotAReservedWord(varName);
			return varName;
		}
	}
	protected function_name(): string {
		return 'create_' + this.var_name().replace(/[^a-z0-9]/gim, '_');
	}

	private is_root() {
		return this._node.parent() === null && this._node.graphNodeId() == this._node.root().graphNodeId();
	}

	private reset() {
		this._lines = [];
	}

	protected add_create(parent_var_name?: string) {
		if (parent_var_name == null) {
			const parent = this._node.parent();
			if (parent) {
				const parent_exporter = CodeExporterDispatcher.dispatch_node(parent, this.options);
				parent_var_name = parent_exporter.var_name();
			}
		}
		const nodeType = this.options.replaceTypeWith || this._node.type();
		const create_node_line = `var ${this.var_name()} = ${parent_var_name}.createNode('${nodeType}')`;
		this._lines.push(create_node_line);
	}

	protected add_name() {
		if (this._node.graphNodeId() !== this._node.root().graphNodeId()) {
			this._lines.push(`${this.var_name()}.setName('${this._node.name()}')`);
		}
	}

	protected add_input() {
		const inputs = this._node.io.inputs.inputs();
		// this._node.io.inputs.inputs().forEach((input: NodeTypeMap[NC] | null, input_index: number) => {

		for (let input_index = 0; input_index < inputs.length; input_index++) {
			const input: BaseNodeByContextMap[NC] | null = inputs[input_index];
			// const input = this._node.input(input_index);
			const input_connection = this._node.io.connections.inputConnection(input_index);
			if (input && input_connection) {
				const output_index = input_connection.output_index;
				const input_exporter = CodeExporterDispatcher.dispatch_node(input, this.options);
				let line;
				if (this._node.io.inputs.hasNamedInputs()) {
					const input_connection_point = this._node.io.inputs.namedInputConnectionPoints()[input_index];
					const output_connection_point = input.io.outputs.namedOutputConnectionPoints()[output_index];
					if (input_connection_point && output_connection_point) {
						const input_name = input_connection_point.name();
						const output_name = output_connection_point.name();
						line = `${this.var_name()}.setInput('${input_name}', ${input_exporter.var_name()}, '${output_name}', ${setInputsOptionsJSON})`;
					}
				} else {
					line = `${this.var_name()}.setInput(${input_index}, ${input_exporter.var_name()}, ${setInputsOptionsJSON})`;
				}
				if (line) {
					this._lines.push(line);
				}
			}
		}
	}
	protected add_comment() {
		const parent = this._node.parent();
		if (parent) {
			// if not root
			let comment = this._node.uiData.comment();
			if (comment) {
				comment = SceneCodeExporter.sanitize_string(comment);
				this._lines.push(`${this.var_name()}.uiData.setComment('${comment}')`);
			}
		}
	}
	protected add_position(options: PositionData = {}) {
		const parent = this._node.parent();
		if (parent) {
			// if not root
			let x_offset;
			const pos = this._node.uiData.position().clone();
			if ((x_offset = options['position_x_offset']) != null) {
				pos.x += x_offset;
			}
			this._lines.push(`${this.var_name()}.uiData.setPosition(${pos.x}, ${pos.y})`);
		}
	}

	protected add_params() {
		let paramsToSave = this._node.params.all;

		if (this.options.replaceTypeWith) {
			const definition = PolyNodeController.definition(this._node.context(), this.options.replaceTypeWith);
			if (definition) {
				const paramsFromDefinition = definition.params || [];
				paramsToSave = paramsToSave.filter((paramToSave) => {
					const paramFromDefinition = paramsFromDefinition.find((p) => p.name == paramToSave.name());
					return paramFromDefinition != null && paramFromDefinition.type == paramToSave.type();
				});
			}
		}

		paramsToSave.forEach((param) => {
			const param_exporter = CodeExporterDispatcher.dispatch_param(param);
			const param_lines: string[] = param_exporter.process();
			param_lines.forEach((param_line) => {
				this._lines.push(param_line);
			});
		});

		// to ensure that gl nodes have the spare params up to date
		this._lines.push(`${this.var_name()}.params.postCreateSpareParams()`);
		// to ensure that gl nodes like the constant and attribute have their inputs/outputs updated
		this._lines.push(`${this.var_name()}.params.runOnSceneLoadHooks()`);
	}

	protected add_input_clonable_state() {
		if (this._node.io.inputs.overrideClonedStateAllowed()) {
			const overriden = this._node.io.inputs.clonedStateOverriden();
			if (overriden) {
				this._lines.push(`${this.var_name()}.io.inputs.overrideClonedState(true)`);
			}
		}
	}
	protected add_flags() {
		this.add_bypass_flag();
		this.add_display_flag();
		this.add_optimize_flag();
	}
	protected add_bypass_flag() {
		if (this._node.flags?.hasBypass()) {
			if (this._node.flags.bypass?.active()) {
				this._lines.push(`${this.var_name()}.flags.bypass.set(true)`);
			}
		}
	}
	protected add_display_flag() {
		if (this._node.flags?.hasDisplay()) {
			const active = this._node.flags.display?.active();
			if (this._node.parent()?.displayNodeController) {
				if (active) {
					this._lines.push(`${this.var_name()}.flags.display.set(true)`);
				}
			} else {
				this._lines.push(`${this.var_name()}.flags.display.set(${active})`);
			}
		}
	}
	protected add_optimize_flag() {
		if (this._node.flags?.hasOptimize()) {
			const active = this._node.flags.optimize?.active();
			if (active) {
				this._lines.push(`${this.var_name()}.flags.optimize.set(true)`);
			}
		}
	}

	protected add_children() {
		const children = ArrayUtils.sortBy(this._node.children(), (child) => child.name());
		const nodes_exporter = new NodesCodeExporter(children);
		nodes_exporter.process(this.var_name()).forEach((child_line) => {
			this._lines.push(child_line);
		});
	}
	// protected add_children_function_declare(){
	// 	const nodes_exporter = new NodesExporter(this._node.children())
	// 	nodes_exporter.create_function_declare().forEach(child_line=>{
	// 		this._lines.push(child_line)
	// 	})
	// }
	// protected add_children_function_call(){
	// 	const nodes_exporter = new NodesExporter(this._node.children())
	// 	nodes_exporter.create_function_call().forEach(child_line=>{
	// 		this._lines.push(child_line)
	// 	})
	// }

	protected add_custom() {}

	protected add_selection() {
		if (this._node.childrenController?.selection != null) {
			for (let node of this._node.childrenController.selection.nodes()) {
				const node_exporter = CodeExporterDispatcher.dispatch_node(node, this.options);
				this._lines.push(`${this.var_name()}.childrenController.selection.add([${node_exporter.var_name()}])`);
			}
		}
	}
}
